import { z } from "zod";
import { type ItineraryDTO } from "../../../types/dtos/api/itinerary/itinerary.dto";
import { definePersistedSlice } from "../runtime/definePersistedSlice";

/**
 * Minimal structural guard for a persisted saved {@link ItineraryDTO}.
 *
 * @remarks Uses permissive `catchall` so the full backend-copied payload is kept as-is on disk
 * without mirroring every DTO field in Zod. Invalid or partial rows are dropped on read.
 */
const PersistableItineraryDtoSchema = z.object( {
    "id":          z.string().min( 1 ),
    "startTime":   z.string().min( 1 ),
    "endTime":     z.string().min( 1 ),
    "finalizedAt": z.string().min( 1 ),
    "timestamp":   z.number(),
    "finalized":   z.boolean(),
    "legs":        z.array( z.unknown() ),
} ).catchall( z.unknown() );

/** Persisted wrapper around a full itinerary snapshot and save metadata. */
export interface SavedItineraryEntry {
    "itinerary": ItineraryDTO;
    "savedAt":   number;

    /** User-facing departure label captured when the itinerary was saved. */
    "originLabel"?: string;

    /** User-facing arrival label captured when the itinerary was saved. */
    "destinationLabel"?: string;
}

const SavedItineraryEntrySchema = z.object( {
    "itinerary":   PersistableItineraryDtoSchema,
    "savedAt":     z.number(),
    "originLabel": z.string().min( 1 ).
        optional(),
    "destinationLabel": z.string().min( 1 ).
        optional(),
} );

/**
 * Drops corrupt saved itinerary rows on read so one invalid entry cannot break the slice.
 *
 * @param items Raw array from storage.
 * @returns Sanitized saved itinerary list.
 */
const sanitizeSavedItineraries = ( items: unknown[] ): SavedItinerariesState => {
    return items.flatMap( ( item ) => {
        const parsed = SavedItineraryEntrySchema.safeParse( item );

        if ( !parsed.success ) {
            return [];
        }

        return [
            {
                "itinerary":        parsed.data.itinerary as ItineraryDTO,
                "savedAt":          parsed.data.savedAt,
                "originLabel":      parsed.data.originLabel,
                "destinationLabel": parsed.data.destinationLabel,
            },
        ];
    } );
};

/**
 * Ordered saved itineraries with invalid entries removed on read.
 *
 * @remarks Stored in IndexedDB because payloads are full {@link ItineraryDTO} snapshots.
 */
const SavedItinerariesSchema = z.array( z.unknown() ).
    default( [] ).
    transform( ( items ) => sanitizeSavedItineraries( items ) );

/** Saved itinerary entries chosen by the user (newest saves first). */
export type SavedItinerariesState = SavedItineraryEntry[];

/** Empty list used as default. */
export const SAVED_ITINERARIES_DEFAULT: SavedItinerariesState = [];

/** Persisted slice: saved itineraries (lazy, IndexedDB). */
export const savedItinerariesSlice = definePersistedSlice<SavedItinerariesState>( {
    "key":            "savedItineraries",
    "currentVersion": 1,
    "storage":        "idb",
    "schema":         SavedItinerariesSchema,
    "default":        () => SAVED_ITINERARIES_DEFAULT,
    "migrations":     {},
} );
